// Audit trail writer — the who-did-what log behind ownership moves, stage
// changes and archival. Ported from legacy logAudit/flushAuditQueue (v20 lines
// ~1058-1093). The actor is always stamped from the SESSION slice and their
// NAME is baked into the summary so the record survives even if the user is
// later removed.
//
// A failed insert is NOT swallowed and NOT lost: the row is pushed to a bounded
// in-memory retry queue and replayed on the next successful audit write (the
// signal the connection is back), while the caller's own write already
// succeeded. The user is told "Saved, but the audit entry failed" — honest, per
// the content rules (never a false all-clear).

import { store, SLICES } from '../state/store.js';
import { getClient, ok, fail } from '../db/client.js';
import { TABLES } from '../db/contract.js';
import { toast } from '../ui/toast.js';

// audit_events.event_type vocabulary the app writes (legacy call sites).
export const AUDIT_EVENT = Object.freeze({
  CASE_CREATED: 'case_created',
  CASE_ASSIGNED: 'case_assigned',
  CASE_STAGE_CHANGED: 'case_stage_changed',
  CASE_ARCHIVED: 'case_archived',
  AE_REPORTED: 'adverse_event_reported', // legacy logAudit('adverse_event_reported', …)
  CONTACT_LOGGED: 'contact_logged', // legacy logAudit('contact_logged', …)
  // Billing (Phase 5B). EXPENSE_DELETED is the legacy string verbatim;
  // EXPENSE_CREATED is new — v_20 logged voids but not entries, so money could
  // appear in the ledger with no record of who put it there.
  EXPENSE_CREATED: 'expense_created',
  EXPENSE_DELETED: 'expense_deleted',
  // Admin console (Phase 6). v_20's logUserActivity() wrote every admin action
  // under this ONE event_type with the detail in the summary; kept verbatim so
  // the two apps' audit trails stay one readable stream during the parallel run.
  DATA_CHANGED: 'data_changed',
});

export const AUDIT_RETRY_CAP = 50; // legacy cap — bounded so an outage can't grow it unbounded

/** @type {any[]} */
const _retry = [];

/** Test-only: drain the retry queue. @returns {void} */
export function _resetAudit() {
  _retry.length = 0;
}

/** Rows currently awaiting replay (inspection/tests). @returns {number} */
export function pendingAuditCount() {
  return _retry.length;
}

/**
 * Build the audit row (pure). occurred_at is stamped client-side with the TRUE
 * event time so rows replayed from the queue hours later keep their real order
 * (the DB's insert-time now() would misorder the ownership timeline). Exact
 * legacy shape.
 * @param {string} eventType
 * @param {string} summary
 * @param {string|null} caseId
 * @param {Record<string, any>|null|undefined} details
 * @param {{id: string|null|undefined, fullName: string|null|undefined}} actor
 * @param {string} nowISO occurred_at timestamp
 * @returns {Record<string, any>}
 */
export function buildAuditRow(eventType, summary, caseId, details, actor, nowISO) {
  const name = actor && actor.fullName;
  const id = (actor && actor.id) || null;
  return {
    event_type: eventType,
    event_source: 'app',
    case_id: caseId || null,
    occurred_at: nowISO,
    actor_user_id: id,
    summary: (name ? name + ' — ' : '') + summary,
    details: Object.assign({}, details || {}, { by: name || null, by_id: id }),
  };
}

function actorFromSession(session) {
  const s = session || store.get(SLICES.SESSION) || {};
  return { id: s.user && s.user.id, fullName: s.fullName };
}

/**
 * Write one audit row. Returns the {ok,data,error,code} envelope. On failure
 * the row is queued for replay and a non-blocking toast fires (the caller's own
 * write already landed).
 * @param {string} eventType see AUDIT_EVENT
 * @param {string} summary human sentence (actor name is prepended)
 * @param {string|null} caseId
 * @param {Record<string, any>} [details]
 * @param {{client?: any, session?: any}} [deps]
 * @returns {Promise<{ok: boolean, data: any, error: any, code: string|null}>}
 */
export async function logAudit(eventType, summary, caseId, details, deps) {
  const client = (deps && deps.client) || getClient();
  const row = buildAuditRow(
    eventType,
    summary,
    caseId,
    details,
    actorFromSession(deps && deps.session),
    new Date().toISOString()
  );
  try {
    // Supabase RESOLVES (does not reject) on a DB error — check .error explicitly
    // or a swallowed failure silently breaks the who-did-what trail.
    const { error } = await client.from(TABLES.AUDIT_EVENTS).insert([row]);
    if (error) throw error;
    await flushAuditQueue(client);
    return ok(row);
  } catch (e) {
    console.warn('logAudit', e);
    if (_retry.length < AUDIT_RETRY_CAP) _retry.push(row);
    toast('Saved, but the audit entry failed', 'err');
    return fail(e);
  }
}

/**
 * Best-effort replay of queued rows. Drains the queue and silently re-queues
 * anything that still fails (no toast storm during a sustained outage).
 * @param {any} client
 * @returns {Promise<void>}
 */
export async function flushAuditQueue(client) {
  if (!_retry.length) return;
  const pending = _retry.splice(0, _retry.length);
  for (const row of pending) {
    try {
      const { error } = await client.from(TABLES.AUDIT_EVENTS).insert([row]);
      if (error) throw error;
    } catch {
      if (_retry.length < AUDIT_RETRY_CAP) _retry.push(row);
    }
  }
}
